<script setup lang="ts">
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Execution } from '@/shared/Executions/models/Execution'

defineProps<{
  execution: Execution | null | undefined
  tenantId: string
  userId: string
}>()

// --- Formatting helpers ---

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function formatFileSize(bytes?: number): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function getStatusBadgeClass(status?: string): string {
  switch (status) {
    case 'succeeded': return 'bg-green-500 hover:bg-green-500 text-white'
    case 'failed': return 'bg-red-500 hover:bg-red-500 text-white'
    case 'running': return 'bg-blue-500 hover:bg-blue-500 text-white'
    case 'pending': return 'bg-yellow-500 hover:bg-yellow-500 text-white'
    case 'cancelled': return 'bg-gray-500 hover:bg-gray-500 text-white'
    case 'throttled': return 'bg-orange-500 hover:bg-orange-500 text-white'
    case 'rejected': return 'bg-red-500 hover:bg-red-500 text-white'
    default: return 'bg-gray-500 hover:bg-gray-500 text-white'
  }
}
</script>

<template>
  <!-- Loading skeleton -->
  <div v-if="!execution" class="grid grid-cols-1 gap-4 md:grid-cols-2">
    <div class="rounded-lg border border-border p-4">
      <Skeleton class="h-5 w-1/3 mb-3" />
      <div class="space-y-2">
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-2/3" />
        <Skeleton class="h-4 w-3/4" />
        <Skeleton class="h-4 w-1/2" />
        <Skeleton class="h-4 w-2/3" />
      </div>
    </div>
    <div class="rounded-lg border border-border p-4">
      <Skeleton class="h-5 w-1/3 mb-3" />
      <div class="space-y-2">
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-2/3" />
      </div>
    </div>
    <div class="rounded-lg border border-border p-4">
      <Skeleton class="h-5 w-1/3 mb-3" />
      <div class="space-y-2">
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-2/3" />
        <Skeleton class="h-4 w-3/4" />
      </div>
    </div>
    <div class="rounded-lg border border-border p-4">
      <Skeleton class="h-5 w-1/3 mb-3" />
      <div class="space-y-2">
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-2/3" />
        <Skeleton class="h-4 w-3/4" />
        <Skeleton class="h-4 w-1/2" />
      </div>
    </div>
  </div>

  <!-- Content -->
  <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
    <!-- Identity -->
    <div class="rounded-lg border border-border p-4">
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide">Identity</h3>
      <dl class="space-y-2 text-sm">
        <div class="flex justify-between">
          <dt class="text-muted-foreground">ID</dt>
          <dd class="font-mono text-foreground">{{ execution.id ?? '—' }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Name</dt>
          <dd class="text-foreground">{{ execution.name ?? '—' }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Status</dt>
          <dd>
            <Badge :class="getStatusBadgeClass(execution.status)">
              {{ execution.status ?? '—' }}
            </Badge>
          </dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Execution Type</dt>
          <dd class="text-foreground">{{ execution.executionType ?? '—' }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Workflow Type</dt>
          <dd class="text-foreground">{{ execution.workflowType ?? '—' }}</dd>
        </div>
      </dl>
    </div>

    <!-- Ownership -->
    <div class="rounded-lg border border-border p-4">
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide">Ownership</h3>
      <dl class="space-y-2 text-sm">
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Tenant ID</dt>
          <dd class="font-mono text-foreground text-right break-all">{{ tenantId }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Owner ID</dt>
          <dd class="font-mono text-foreground text-right break-all">{{ userId }}</dd>
        </div>
      </dl>
    </div>

    <!-- File Information -->
    <div class="rounded-lg border border-border p-4">
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide">File Information</h3>
      <dl class="space-y-2 text-sm">
        <div class="flex justify-between">
          <dt class="text-muted-foreground">File Name</dt>
          <dd class="text-foreground text-right break-all">{{ execution.input?.fileName ?? '—' }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">File Size</dt>
          <dd class="text-foreground">{{ formatFileSize(execution.input?.fileSize) }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">File ID</dt>
          <dd class="font-mono text-foreground text-right break-all">{{ execution.input?.fileId ?? '—' }}</dd>
        </div>
      </dl>
    </div>

    <!-- Timestamps -->
    <div class="rounded-lg border border-border p-4">
      <h3 class="mb-3 text-sm font-semibold text-foreground uppercase tracking-wide">Timestamps</h3>
      <dl class="space-y-2 text-sm">
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Created</dt>
          <dd class="text-foreground">{{ formatDate(execution.createdUtc) }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Started</dt>
          <dd class="text-foreground">{{ formatDate(execution.startedUtc) }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Completed</dt>
          <dd class="text-foreground">{{ formatDate(execution.completedUtc) }}</dd>
        </div>
        <div class="flex justify-between">
          <dt class="text-muted-foreground">Duration</dt>
          <dd class="text-foreground">{{ execution.durationHuman ?? '—' }}</dd>
        </div>
      </dl>
    </div>
  </div>
</template>
